import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { fetchRevealLimboStats } from "../services/analyticsService";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig, Delta } from "./PanelHeadline";

// The "limbo window" (2026-08-27): time from the FIRST member selecting
// Reveal Together to the mutual reveal completing. This is the number the
// vault-nudge and next-introduction push timing get tuned against. Test
// accounts are excluded server-side (QA reveals complete instantly and
// would poison the average).
const fmtMin = (m) => {
    if (m === null || m === undefined) return "n/a";
    if (m < 90) return `${m} min`;
    if (m < 48 * 60) return `${Math.round((m / 60) * 10) / 10} h`;
    return `${Math.round((m / 1440) * 10) / 10} d`;
};

const RevealLimboStats = ({ startLoading, stopLoading }) => {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            startLoading();
            setFailed(false);
            const res = await fetchRevealLimboStats();
            setData(res?.data?.data);
        } catch (err) {
            console.error("Failed to load reveal limbo stats", err);
            setFailed(true);
        } finally {
            stopLoading();
        }
    };

    const cards = [
        {
            title: "Average limbo",
            value: fmtMin(data?.avgMinutes),
            description: `First Reveal tap to mutual reveal, averaged over ${data?.revealedPairs || 0
                } real revealed pair${data?.revealedPairs === 1 ? "" : "s"} in the last ${data?.days || 30
                } days. Median ${fmtMin(data?.medianMinutes)}.`,
            icon: "mdi:timer-sand",
            bg: "bg-purple",
            gradient: "bg-gradient-start-2",
        },
        {
            title: "Slowest 10 percent",
            value: fmtMin(data?.p90Minutes),
            description:
                "Nine of ten mutual reveals complete faster than this. The tail is where reminder timing earns its keep.",
            icon: "mdi:timer-alert-outline",
            bg: "bg-warning-main",
            gradient: "bg-gradient-start-3",
        },
        {
            title: "In limbo right now",
            value: `${data?.pendingNow ?? 0}`,
            description:
                data?.pendingNow > 0
                    ? `One side has chosen Reveal Together and is waiting on the other. Oldest wait: ${fmtMin(
                        data?.pendingOldestMinutes
                    )}.`
                    : "No one is currently waiting on a partner's choice.",
            icon: "mdi:account-clock",
            bg: "bg-info-main",
            gradient: "bg-gradient-start-5",
        },
    ];

    return (
        <AnalyticsSection
            id="limbo"
            icon="mdi:timer-sand"
            title="Reveal limbo"
            basis="Time between the first Reveal choice and the mutual reveal. Test accounts excluded. QA reveals are excluded from the average because they are staged instantly."
        >

            <PanelHeadline
                failed={failed}
                failedText="Could not load reveal limbo. The tiles below are blank, not zero."
                empty={!data}
                emptyText="No mutual reveals recorded yet."
            >
                Half of mutual reveals finish inside{" "}
                <Fig>{fmtMin(data?.avgMinutes)}</Fig>
                {data?.pendingNow > 0 ? (
                    <>
                        , but <Fig>{data.pendingNow}</Fig>{" "}
                        {data.pendingNow === 1 ? "person is" : "people are"} waiting right
                        now.
                    </>
                ) : (
                    <>, and nobody is waiting right now.</>
                )}
            </PanelHeadline>

            {failed && (
                <div className="alert alert-warning py-2 px-3 mb-3">
                    Could not load reveal limbo data.
                </div>
            )}

            <div className="row row-cols-lg-3 row-cols-1 gy-4 mb-2">
                {cards.map((card, idx) => (
                    <div className="col" key={idx}>
                        <div
                            className={`card shadow-none border ${card.gradient} h-100 position-relative`}
                        >
                            <div className="card-body p-20 position-relative">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">
                                            {card.title}
                                        </p>
                                        <h6 className="mb-0">{card.value}</h6>
                                    </div>
                                    <div
                                        className={`w-50-px h-50-px ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                                    >
                                        <Icon
                                            icon={card.icon}
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>
                                <p className="fw-medium text-sm text-primary-light mt-12 mb-0">
                                    {card.description}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </AnalyticsSection>
    );
};

export default RevealLimboStats;
